import i18n from '../../../i18n'
import { toolbarSettings } from '@/components/global/TinyMCE/toolbarSettings'
import { insertDetailsMenu } from '@/components/global/TinyMCE/insertDetailsMenu'
import { fontColors } from '@/components/global/TinyMCE/fontColors'
import { loadLocalPlugins } from '@/components/global/TinyMCE/loadLocalPlugins'
import { skinLoadPatch } from '@/components/global/TinyMCE/skinLoadPatch'
import { remindUserToEditCaseDetailsInSidebar } from '@/components/global/TinyMCE/remindUserToEditCaseDetailsInSidebar'
import { commandHandler } from '@/components/global/TinyMCE/commandHandler'
import { pasteHandler } from '@/components/global/TinyMCE/pasteHandler'
import { applyStylingToNonEditable } from '@/utils/tinyMCE/plugins/applyStylingToNonEditable'
import { customToolbarButtons } from '@/utils/tinyMCE/plugins/customToolbarButtons'
import { persistStylesOnBackspace } from '@/utils/tinyMCE/plugins/persistStylesOnBackspace'
import { getContentStyles } from '@/utils/tinyMCE/updateDynamicText/getTinyMCESettingsContentStyles'
import { setupServiceTemplateStylePreservation } from '@/utils/tinyMCE/stylePreservation'
import { DEFAULT_LOCALE_CODE } from '@messenger/consts'
import {
  TINYMCE_BOX_SCHEMA,
  loadCaseFieldXamlAsHtml,
  refuseActiveElements,
} from './tinyMCESchema'

export const useTinyMCESettings = (props) => {
  const {
    additionalInit = {},
    inline = true,
    caseDetails,
    funeralHomeDetails,
    dateFormat,
    theme,
    selectedTheme,
    isVerse,
    setToast,
    updateHeight,
    user,
    pageSetLocale,
    onCaseDetailTemplateInserted,
    plainTextMode = false,
  } = props

  const editorData = {
    caseDetails,
    funeralHomeDetails,
    dateFormat,
    selectedTheme,
    isVerse,
    setToast,
    updateHeight,
    user,
    pageSetLocale,
    onCaseDetailTemplateInserted,
    plainTextMode,
  }

  // Determine TinyMCE language code
  const tinyMceLang = i18n.language === 'fr' ? 'fr_FR' : i18n.language

  const languageURL =
    tinyMceLang === DEFAULT_LOCALE_CODE
      ? undefined
      : `/tinymce/langs/${tinyMceLang}.js`

  const dontKeepStylesInPlainTextMode = !plainTextMode

  return {
    height: '100%',
    width: '100%',
    browser_spellcheck: true,
    inline: inline,
    forced_root_block: 'p',
    forced_root_block_attrs: {
      style: 'margin: 0;',
    },
    encoding: 'xml',
    entity_encoding: 'raw',
    fix_list_elements: true,
    keep_styles: dontKeepStylesInPlainTextMode,
    // elements like <span style="..."><br data-mce-bogus="1" /></span> usually get removed by tinymce
    remove_trailing_brs: false,
    ...TINYMCE_BOX_SCHEMA,
    visual_table_class: 'border-0',
    visual: true,
    menubar: false,
    statusbar: false,
    toolbar: toolbarSettings(inline),
    language: tinyMceLang,
    language_url: languageURL,
    setup: (editor) => {
      const editorProps = { editor, ...editorData }
      return setupTinyMCE(editorProps)
    },
    fontsize_formats:
      '5pt 6pt 7pt 8pt 9pt 10pt 11pt 12pt 14pt 16pt 18pt 20pt 22pt 24pt 26pt 28pt 30pt 32pt 34pt 36pt 40pt 42pt 46pt 48pt 50pt 55pt 60pt 65pt 70pt 75pt 80pt 85pt 90pt 95pt 100pt 110pt 120pt 200pt 210pt 220pt 300pt 310pt 320pt 340pt 350pt 400pt 410pt 420pt 430pt 440pt',
    skin: theme === 'dark' ? 'oxide-dark' : 'oxide',
    content_css: theme === 'dark' ? 'dark' : 'default',
    content_style: getContentStyles(),
    color_map: [...fontColors({ selectedTheme })],
    plugins: [
      'image',
      'template',
      'noneditable',
      'table',
      'paste',
      'hr',
      'casedetails',
    ],
    paste_as_text: true,
    lineheight_formats:
      '0.5 0.75 0.85 0.95 1 1.15 1.25 1.35 1.5 1.75 2 2.25 2.5 2.75 3 3.25 3.5 3.75 4',
    ...additionalInit,
  }
}

const setupTinyMCE = (editorProps) => {
  const {
    editor,
    setToast,
    selectedTheme,
    isVerse,
    updateHeight,
    onCaseDetailTemplateInserted,
    plainTextMode = false,
  } = editorProps
  refuseActiveElements({ editor })
  loadCaseFieldXamlAsHtml({ editor })
  loadLocalPlugins({ editor, updateHeight })
  applyStylingToNonEditable({ editor, selectedTheme, isVerse, plainTextMode })
  persistStylesOnBackspace({ editor, plainTextMode })
  insertDetailsMenu({ editor })
  customToolbarButtons({ editor })
  skinLoadPatch({ editor })
  remindUserToEditCaseDetailsInSidebar({ editor, setToast })
  commandHandler({ editor, updateHeight, onCaseDetailTemplateInserted })
  pasteHandler({ editor, plainTextMode })
  setupServiceTemplateStylePreservation(editor)
}

export default useTinyMCESettings
